import React, { useState, useEffect, useMemo } from "react";
import { MdClose } from "react-icons/md";
import { FaRegCheckSquare, FaRegSquare } from "react-icons/fa";
import { CircularProgress, Tabs, Tab, Box } from "@mui/material";
import tw from "tailwind-styled-components";
import { Label, ScrollableContent } from "../Styles/InputStyles";
import { GoSearch } from "react-icons/go";
import { getRole, getUserType } from "../../helpers";
import { useSelector } from "react-redux";

export default function PaidToUserModal({
    isOpen,
    onClose,
    onSelect,
    options = [],
    landlordList = [],
    selectedPaidTo,
    isLoading = false,
    title
}) {
    const [searchText, setSearchText] = useState("");
    const [activeTab, setActiveTab] = useState(0);
    const vendorTypes = useSelector(
        (state) => state.VendorTypesReducer.vendorTypes
    );

    const getVendorTypeName = (typeId) => {
        const type = vendorTypes?.find(t => t.id === typeId);
        return type ? type.name : '';
    };

    const getTitle = (activeTab, item) => {
        if (activeTab === 2)
            return "Landlord";
        else if (activeTab === 3)
            return "Bank";
        else if (activeTab === 1)
            return getVendorTypeName(item?.type || 1);
        else
            return getRole(item?.role || 1);
    };

    const getTabForSelectedPaidTo = (selected) => {
        if (!selected) return 0;

        const option = options.find(opt => opt.id === selected.id);
        if (!option) return 0;

        if (option.userType === 2) return 0;
        if ((option.userType === 4 && option.type !== 2 && option.type !== 24)) return 1;
        if (option.userType === 4 && option.type === 2) return 2;
        if (option.userType === 4 && option.type === 24) return 3;

        return 0;
    }

    useEffect(() => {
        if (isOpen) {
            setSearchText("");
            const tabForSelected = getTabForSelectedPaidTo(selectedPaidTo);
            setActiveTab(tabForSelected);
        }
    }, [isOpen, selectedPaidTo]);

    const combinedOptions = useMemo(() => {
        if (activeTab === 2) {
            return landlordList.map(landlord => ({
                ...landlord,
                userType: 5,
                type: 2,
                id: landlord.id || landlord.landlordId,
                name: landlord.name || landlord.landlordName,
                mobile: landlord.mobile || landlord.phone,
                email: landlord.email,
                address: landlord.address
            }));
        }
        return options;
    }, [activeTab, options, landlordList]);

    const filteredByTab = useMemo(() => {
        return combinedOptions.filter(option => {
            switch (activeTab) {
                case 0:
                    return getUserType(option.userType) === "Staff";
                case 1:
                    return (option.userType === 4 && option.type !== 2 && option.type !== 24);
                case 2:
                    return true;
                case 3:
                    return option.userType === 4 && option.type === 24;
                default:
                    return false;
            }
        });
    }, [combinedOptions, activeTab]);

    const groupedUsers = useMemo(() => {
        const groups = {};

        filteredByTab.forEach(option => {
            let groupName;

            switch (activeTab) {
                case 0:
                    groupName = "Staff";
                    break;
                case 1:
                    groupName = "Vendors";
                    break;
                case 2:
                    groupName = "Landlords";
                    break;
                case 3:
                    groupName = "Banks";
                    break;
                default:
                    groupName = getUserType(option.userType || 0);
            }

            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push({
                ...option,
                label: option.name,
                value: option.id,
                userTypeLabel: groupName
            });
        });

        return Object.keys(groups).map(groupName => ({
            name: groupName,
            items: groups[groupName]
        }));
    }, [filteredByTab, activeTab]);

    const filteredUsers = useMemo(() => {
        return groupedUsers.map(group => ({
            ...group,
            items: group.items.filter(user =>
                user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                user.mobile?.toLowerCase().includes(searchText.toLowerCase()) ||
                user.userTypeLabel?.toLowerCase().includes(searchText.toLowerCase()) ||
                getTitle(activeTab, user)?.toLowerCase().includes(searchText.toLowerCase())
            )
        })).filter(group => group.items.length > 0);
    }, [groupedUsers, searchText, activeTab]);

    const handleSelect = (user) => {
        onSelect(user);
        onClose();
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSearchText("");
    };

    const isSelected = (userId) => {
        return selectedPaidTo?.id === userId;
    };

    const getEmptyMessage = () => {
        switch (activeTab) {
            case 0: return "No staff available";
            case 1: return "No vendors available";
            case 2: return "No landlords available";
            case 3: return "No banks available";
            default: return "No users available";
        }
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const getTabLabel = (tabIndex) => {
        switch (tabIndex) {
            case 0: return "Staff";
            case 1: return "Vendor";
            case 2: return "Landlord";
            case 3: return "Bank";
            default: return "";
        }
    };

    if (!isOpen) return null;

    return (
        <Overlay>
            <ModalWrapper>
                <Header>
                    <h1 className="text-white text-lg font-medium">{title}</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </Header>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '14px',
                                minWidth: 'auto',
                                px: 2,
                            },
                            '& .Mui-selected': {
                                color: '#3B82F6 !important',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#3B82F6',
                            }
                        }}
                    >
                        <Tab label="Staff" />
                        <Tab label="Vendor" />
                        <Tab label="Landlord" />
                        <Tab label="Bank" />
                    </Tabs>
                </Box>

                <div className="p-4 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder={`Search ${getTabLabel(activeTab).toLowerCase()}...`}
                            value={searchText}
                            onChange={handleSearchChange}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />

                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>

                <ScrollableContent className="max-h-[calc(100dvh-200px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {filteredByTab.length === 0 ? getEmptyMessage() : "No users found"}
                        </div>
                    ) : (
                        filteredUsers.map((group) => (
                            <div key={group.name}>
                                {/* Optional: Show group header if multiple groups exist */}
                                {groupedUsers.length > 1 && (
                                    <div className="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-600">
                                        {group.name}
                                    </div>
                                )}

                                {group.items.map((user) => (
                                    <OptionRow
                                        key={user.id}
                                        onClick={() => handleSelect(user)}
                                    >
                                        <div className="flex flex-col flex-1">
                                            <Label className="font-medium text-sm">{user.name}</Label>
                                            <div className="flex gap-2 text-xs text-gray-500 mt-1">
                                                {user.mobile && (
                                                    <>
                                                        <span>#{user.mobile}</span>
                                                        <span>•</span>
                                                    </>
                                                )}
                                                <span>{getTitle(activeTab, user)}</span>
                                            </div>
                                        </div>
                                        <button disabled={isLoading}>
                                            {isLoading ? (
                                                <CircularProgress size={20} />
                                            ) : isSelected(user.id) ? (
                                                <FaRegCheckSquare size={20} className="text-green-500 font-normal text-sm" />
                                            ) : (
                                                <FaRegSquare size={20} className="text-gray-300 font-normal text-sm" />
                                            )}
                                        </button>
                                    </OptionRow>
                                ))}
                            </div>
                        ))
                    )}
                </ScrollableContent>
            </ModalWrapper>
        </Overlay>
    );
}

const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`
const OptionRow = tw.div`ml-2 flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50`